import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useHolidayMaster } from "../../contexts/HolidayContext";
import Modal from "../../components/UI/Modal";
import HolidayForm from "../../components/Form/HolidayForm";
import BulkHolidayForm from "../../components/Form/BulkHolidayForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { Description } from "@headlessui/react";
import ReactPaginate from 'react-paginate';
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteHoliday }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteHoliday(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const HolidayMasters = React.memo(() => {
  const { holidays, fetchHolidays, addHoliday, updateHoliday, deleteHoliday, pagination, createBulkHoliday, isLoading } = useHolidayMaster();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);


  
  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingHoliday(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  // const openModalForBulkAdd = useCallback(() => {
  //   setShowModal(true);
  // }, []);

  const openModalForEdit = useCallback((holiday) => {
    // console.log("edit workplace: ", holiday)
    setEditingHoliday(holiday);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingHoliday(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  // create and update function
  const handleFormSubmit = useCallback(
    async (holiday) => {
      setLoading(true);
      try {
        if (editingHoliday) {
          // console.log("holiday payload", holiday);
        
          // Convert FormData to object
          // console.log(holiday instanceof FormData)
          const holidayData = holiday instanceof FormData ? convertFormDataToObject(holiday) : holiday;
  
          // console.log("Payload being sent:", { ...editingHoliday, ...holidayData });
  
          const result = await updateHoliday({ ...editingHoliday, ...holidayData });
          // console.log("result in holiday update: ", result)
          if (result?.success === true) {
            // setNotification("Holiday updated successfully.");
            setNotification({
              message: "Holiday updated successfully.",
              type: "success",
            });
            closeModal();
          } else {
            // setNotification(`Error: ${result?.message || "Failed to update holiday."}`);
            setNotification({
              message: `Error: ${result?.message || "Failed to update holiday."}`,
              type: "error",
            });
          }
          // setMessage("Holiday updated successfully.");
          // setNotification("Holiday updated successfully.");
        } else {
          // try {
            // Convert FormData to object
            const result = await addHoliday(holiday);
            // console.log("error in holiday: ", result)
            if (result?.success === true) {
              // setNotification("Holiday created successfully.");
              setNotification({
                message: "Holiday created successfully.",
                type: "success",
              });
              closeModal();
            } else {
              // setNotification(`Error: ${result?.message || "Failed to add holiday."}`);
              setNotification({
                message: `Error: ${result?.message || "Failed to add holiday."}`,
                type: "error",
              });
            }

        }
        // closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
          // setNotification(err.message);
          setNotification({
            message: err.message,
            type: "error",
          });
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: `"Unexpected error during submission:, ${err}`,
            type: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [editingHoliday, addHoliday, updateHoliday, closeModal]
  );

  // Memoize holidays names
  const holidayForTable = useMemo(
    () =>
        holidays.map((holiday) => ({
        ...holiday,
        day: holiday?.day || "Unknown Holiday",
        date: holiday?.date || "Unknown Holiday",
        occasion: holiday?.occasion || "Unknown Holiday",
      })),
    [holidays]
  );

  // delete function
  const handleDeleteHoliday = async (id) => {
    try {
      await deleteHoliday(id);
      // setNotification("Holiday deleted successfully");
      setNotification({
        message: "Holiday deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete holiday");
      setNotification({
        message: "Failed to delete holiday",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Day", field: "day" },
      { label: "Date", field: "date" },
      { label: "occasion", field: "occasion" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteHoliday={handleDeleteHoliday}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteHoliday,
    ]
  );
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchHolidays(newPage);
    }
  };

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Holiday Masters</h2>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <button
          onClick={openModalForAdd}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
        >
          Add Holiday
        </button>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
        >
          Add Bulk Holiday
        </button>
        </div>
      </div>

      {/* Candidates Table */}
      <ReusableTable
        data={holidayForTable}
        columns={columns}
        loading={isLoading}
        filterable={true}
        excludeFilterColumns={["actions"]}
        onFilterChange={(filtering) => setIsFiltering(filtering)}
      />
      
      {/* Pagination */}
      {!isFiltering && holidayForTable?.length > 0 && (
        <ReactPaginate
          breakLabel="..."
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          pageCount={pagination.totalPages}
          renderOnZeroPageCount={null}
          nextLabel="Next >"
          previousLabel="< Prev"
          previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
          nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          containerClassName="flex items-center justify-center mt-4"
          pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
        />
      )}

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingHoliday ? "Edit Holiday" : "Add Holiday"}>

        <HolidayForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingHoliday || { date: "", occasion: "" }}
          errors={formErrors}
        />
      </Modal>
      
      {/* Modal for Bulk create */}
      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        title="Add Holiday in Bulk">

        <BulkHolidayForm
          loading={false}
          closeModal={() => setShowModal(false)}
          onSubmit={async (holidays) => {
            const result = await createBulkHoliday(holidays);
            // console.log("result in bulk create: ", result)
            if (result?.success === true) {
              setNotification("Holidays created successfully.");
              setShowModal(false); 
            } else {
              setNotification(`Error: ${result?.message || "Failed to create holidays in bulk."}`);
            }
            // setNotification("Holidays Created")
            // setShowModal(false);  
        }}
        />
      </Modal>

    </div>
  );
});

export default HolidayMasters;
